import { useState } from "react";
import { AdminModules } from "../features";
import { DetailsView } from "./detailsView";
import { useNavigate } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { useApolloQuery } from "../../../../hooks";
import dayjs from "dayjs";
import {
  ConfirmationDeleteModal,
  useLanguageTranslator,
} from "../../../../components";
import { useAuthSession } from "../../../../components/AuthSessionProvider";
import { useNotify } from "../../../../components/@common/NotificationsProvider";
import {
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const GET_INDICATORS_WITH_PAGINATION = gql`
  query GetIndicatorsWithPagination(
    $page: Int!
    $themeId: Int
    $indicatorCategoryId: Int
  ) {
    indicatorsWithPagination(
      page: $page
      themeId: $themeId
      indicatorCategoryId: $indicatorCategoryId
    ) {
      collection {
        createdAt
        id
        indicatorCategoryId
        label
        question
        ratingType
        themeId
        updatedAt
      }
      metadata {
        currentPage
        limitValue
        totalCount
        totalPages
      }
    }
  }
`;

const GET_INDICATOR_CATEGORIES = gql`
  query GetIndicatorCategories {
    indicatorCategories {
      id
      name
    }
  }
`;

const DELETE_INDICATOR = gql`
  mutation DeleteIndicator($id: ID!) {
    indicatorDelete(input: { id: $id }) {
      clientMutationId
      indicator {
        createdAt
        id
        indicatorCategoryId
        label
        peopleSource
        policySource
        practiceSource
        question
        ratingType
        themeId
        updatedAt
      }
    }
  }
`;

const policyFormOptionsQueryDocument = gql`
  query policyFormOptionsQuery {
    themeOptions: themes {
      value: id
      label: title
    }
  }
`;

function AdminIndicators() {
  const [filters, setFilters] = useState({
    indicatorCategoryId: "",
    themeId: "",
  });
  const [isOpen, setIsOpen] = useState({ status: false, data: {} });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isOpenDelete, setIsOpenDelete] = useState({ status: false, data: {} });

  const navigate = useNavigate();
  const { user } = useAuthSession() as any;
  const { translator } = useLanguageTranslator();
  const notify = useNotify();

  const { data: indicatorCategoriesData } = useApolloQuery({
    queryDocument: GET_INDICATOR_CATEGORIES,
    isPrivateCall: true,
  });

  const {
    data: indicatorsList,
    loading,
    refetch,
  } = useApolloQuery({
    queryDocument: GET_INDICATORS_WITH_PAGINATION,
    isPrivateCall: true,
    variables: {
      page: page,
      ...(filters?.indicatorCategoryId && filters?.indicatorCategoryId !== "0"
        ? { indicatorCategoryId: Number(filters?.indicatorCategoryId) }
        : {}),
      ...(filters?.themeId && filters?.themeId !== "0"
        ? { themeId: Number(filters?.themeId) }
        : {}),
    },
  });

  const [deleteIndicator, { loading: deleteIndicatorLoading, error }] =
    useMutation(DELETE_INDICATOR, {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    });

  const modifiedGetIndicatorsList =
    indicatorsList?.indicatorsWithPagination?.collection?.map((item: any) => {
      return {
        sr: item?.id,
        id: item?.id,
        question: item?.question,
        label: item?.label,
        themeId: item?.themeId,
        ratingType: item?.ratingType,
        category: indicatorCategoriesData?.indicatorCategories?.find(
          (categoryItem: any) => categoryItem?.id === item?.indicatorCategoryId
        )?.name,
        createdAt: dayjs(item?.createdAt?.split("T")?.[0] || "").format(
          "DD.MM.YYYY"
        ),
        updatedAt: dayjs(item?.updatedAt?.split("T")?.[0] || "").format(
          "DD.MM.YYYY"
        ),
      };
    });

  async function onClickHandler(type: string, clickedItem: any) {
    if (type === "view") {
      setIsOpen({ status: true, data: clickedItem });
      return;
    }
    if (type === "edit") {
      navigate(`/admin/indicators/update?id=${clickedItem}`);
      return;
    }

    if (type === "delete") {
      setIsOpenDelete({ status: true, data: { id: clickedItem } });
    }
  }

  function handleCancel() {
    setIsOpen({ status: false, data: {} });
  }

  const columns = [
    { id: "sr", label: "ID", minWidth: "100%", align: "left" },
    {
      id: "question",
      label: translator("Question"),
      minWidth: "100%",
      align: "left",
    },
    {
      id: "label",
      label: translator("Label"),
      minWidth: "100%",
      align: "left",
    },
    {
      id: "category",
      label: translator("dashboardTableHead.Category"),
      minWidth: "100%",
      align: "left",
    },
    {
      id: "createdAt",
      label: translator("dashboardTableHead.Date Created"),
      minWidth: "100%",
      align: "left",
    },
    {
      id: "updatedAt",
      label: translator("dashboardTableHead.Date Updated"),
      minWidth: "100px",
      align: "left",
    },
    {
      id: "id",
      label: translator("dashboardTableHead.Action"),
      minWidth: "100%",
      align: "right",
    },
  ];

  async function onClickDeleteHandler(item: any) {
    const response = await deleteIndicator({
      variables: { id: Number(item?.id) },
    });
    if (response?.data) {
      notify.success(`Indicator Deleted successfully!`);
      setIsOpenDelete({ status: false, data: {} });
      setIsOpen({ status: false, data: {} });
      setTimeout(async () => {
        await refetch();
      }, 1000);
    }
  }

  function handleDeleteClose() {
    setIsOpenDelete({ status: false, data: {} });
  }

  function onClickDetailHandler(item: any) {
    setIsOpenDelete({ status: true, data: { id: item?.id } });
  }

  const { data } = useApolloQuery({
    queryDocument: policyFormOptionsQueryDocument,
  });

  const handleChange = (event: any) => {
    setFilters({ ...filters, [event.target.name]: event.target.value });
  };

  return (
    <>
      <ConfirmationDeleteModal
        isOpen={isOpenDelete?.status}
        item={isOpenDelete?.data}
        onClickHandler={onClickDeleteHandler}
        handleClose={handleDeleteClose}
      />
      <DetailsView
        isOpen={isOpen?.status}
        item={isOpen?.data}
        handleCancel={handleCancel}
        onClickHandler={onClickDetailHandler}
      />
      <AdminModules
        title={translator("Indicators")}
        path="/admin/indicators/add-new"
        data={modifiedGetIndicatorsList}
        moduleKey="country-module"
        page={page}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        setPage={setPage}
        onClickHandler={onClickHandler}
        metadata={indicatorsList?.indicatorsWithPagination?.metadata || {}}
        isView={true}
        tableColumns={columns}
        isAccounts={true}
      >
        <Stack direction={"row"} justifyContent={"center"} mt={1}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ backgroundColor: "white" }}
            px={3}
          >
            <Typography variant="body2" color="textSecondary">
              {translator("Filter by")}:
            </Typography>
            <FormControl sx={{ minWidth: 200 }}>
              {/* <InputLabel>
              {" "}
              {translator("dashboardTableHead.Category")}
            </InputLabel> */}
              <Autocomplete
                options={[
                  { id: "0", name: translator("All") },
                  ...(indicatorCategoriesData?.indicatorCategories || []),
                ]}
                getOptionLabel={(option: any) => option.name}
                onChange={(event, newValue) =>
                  handleChange({
                    target: {
                      name: "indicatorCategoryId",
                      value: newValue?.id || "",
                    },
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translator("dashboardTableHead.Category")}
                    variant="outlined"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </FormControl>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>{translator("Theme")}</InputLabel>
              <Select
                sx={{ width: 200 }}
                label="Theme"
                name="themeId"
                onChange={handleChange}
              >
                {[
                  { value: "0", label: translator("All") },
                  ...(data?.themeOptions || []),
                ]?.map((op: any, i: any) => (
                  <MenuItem key={`${op.value}-${i}`} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </AdminModules>
    </>
  );
}

export { AdminIndicators };
